import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

import type { ResearchRoomMessage } from '@repo/contracts';
import { estimateResearchRoundCredits } from '@repo/contracts/credits';

import { trackEvent } from '../analytics/posthog';
import { CreditDot } from '../credits/credit-chip';
import { useCredits } from '../credits/use-credits';

/** Below this many questions' worth, the composer says how many are left. */
const LOW_QUESTIONS = 3;

function credits(amount: number): string {
  return `${String(amount)} credit${amount === 1 ? '' : 's'}`;
}

/** Where a round's price is drawn: in the footer of Floto's read of the
 *  round, or — for a poll, or a round with no read — as a line of its own. */
export interface RoundCostSpot {
  amount: number;
  inCard: boolean;
}

/**
 * What each round cost, keyed by the message it is drawn on.
 *
 * On Floto's summary card when the round shows one — the read is the round's
 * last word, so the price there reads as the price of everything above it,
 * never of the question that comes next. Failed reads still carry it: the
 * answers were given and paid for. Otherwise under the round's last line.
 *
 * The price lives on the question or poll that opened the round; a round
 * with no price (held back, nobody answered) draws nothing.
 */
export function roundCosts(
  messages: ResearchRoomMessage[],
): Map<string, RoundCostSpot> {
  const rounds = new Map<
    number,
    { credits?: number; lastId: string; summaryId?: string }
  >();
  for (const message of messages) {
    const round = rounds.get(message.round) ?? { lastId: message.id };
    round.lastId = message.id;
    // A quiet read is not drawn, so the price cannot sit in it.
    if (message.kind === 'summary' && !message.quiet) {
      round.summaryId = message.id;
    }
    if (
      (message.kind === 'question' || message.kind === 'poll') &&
      message.credits
    ) {
      round.credits = message.credits;
    }
    rounds.set(message.round, round);
  }
  const costs = new Map<string, RoundCostSpot>();
  for (const { credits, lastId, summaryId } of rounds.values()) {
    if (!credits) continue;
    costs.set(summaryId ?? lastId, {
      amount: credits,
      inCard: summaryId !== undefined,
    });
  }
  return costs;
}

/**
 * A round's price, quiet — there to be read, not to interrupt. Labelled with
 * what it covers, so it is never mistaken for the price of what follows.
 */
export function RoundCost({ amount, inCard }: RoundCostSpot) {
  return (
    <p
      className={[
        'flex items-center gap-1.5 text-tag text-text-muted tabular-nums',
        inCard ? '' : 'px-1',
      ].join(' ')}
      title="The question, every answer, and Floto's read"
    >
      <CreditDot />
      {inCard ? 'Round cost' : 'This round'} · {credits(amount)}
    </p>
  );
}

/**
 * Where the room stands on credits, for the composer: whether the next
 * question can be paid for, and how much it is likely to cost.
 *
 * Checked against the same estimate the server refuses on, so the composer
 * and the API never disagree about whether a question can be asked. While
 * the balance is still loading the room stays open — the server is the gate,
 * and a slow balance must not lock anyone out.
 */
export function useRoomCredits(
  lastRoundCredits: number | undefined,
  /** A refusal the server already gave, which may know a higher figure. */
  shortfall: { available: number; required: number } | null,
) {
  const { balance, loaded } = useCredits();
  const estimate = Math.max(
    estimateResearchRoundCredits(lastRoundCredits),
    shortfall?.required ?? 0,
  );
  // Unknown (still loading, never refused) is open, not blocked.
  const available = loaded ? balance.totalCredits : shortfall?.available;
  const blocked = available !== undefined && available < estimate;
  const questionsLeft = loaded
    ? Math.floor(balance.totalCredits / estimate)
    : null;
  return {
    available: available ?? 0,
    blocked,
    estimate,
    loaded,
    low:
      !blocked && questionsLeft !== null && questionsLeft < LOW_QUESTIONS
        ? questionsLeft
        : null,
  };
}

/**
 * The balance, where it is spent: a quiet line under the composer. The
 * room's only place for it — floating over the board, it read as an account
 * badge. No per-question price: a round costs what its tokens do, which
 * varies too much for one number to mean anything; each round's own cost is
 * in the thread.
 */
export function RoomCreditsLine({ available }: { available: number }) {
  return (
    <p className="mt-2 flex items-center justify-end gap-1.5 px-1 text-tag text-text-muted tabular-nums">
      <CreditDot />
      {available.toLocaleString()} credit{available === 1 ? '' : 's'} left
    </p>
  );
}

/** Billing's back link, to this room rather than home. */
function useBackToRoom() {
  const { pathname, search } = useLocation();
  return { label: 'Back to room', to: `${pathname}${search}` };
}

/** A line above the composer once only a couple of questions' worth is
 *  left — said before it runs out, not after. */
export function LowCreditsNote({
  estimate,
  questionsLeft,
}: {
  estimate: number;
  questionsLeft: number;
}) {
  const backToRoom = useBackToRoom();
  return (
    <p className="mb-2 flex items-center gap-1.5 text-caption text-text-secondary">
      <CreditDot tone="alarm" />
      About {questionsLeft} question{questionsLeft === 1 ? '' : 's'} left · ~
      {credits(estimate)} each
      <Link
        className="ml-auto font-semibold text-brand underline-offset-2 hover:underline"
        onClick={() => {
          trackEvent('top_up_clicked', {
            questions_left: questionsLeft,
            source: 'room_low',
          });
        }}
        state={{ back: backToRoom, from: 'room_low' }}
        to="/billing"
      >
        Top up
      </Link>
    </p>
  );
}

/**
 * The composer's place when the next question cannot be paid for.
 *
 * Everything already said stays readable, and ending the session to get the
 * report is still there — only asking more needs credits.
 */
export function RoomOutOfCredits({
  available,
  estimate,
}: {
  available: number;
  estimate: number;
}) {
  const backToRoom = useBackToRoom();
  // Where the wall is met, as `OutOfCredits` records it for the other panels.
  useEffect(() => {
    trackEvent('insufficient_credits_hit', {
      balance: available,
      cost: estimate,
      feature: 'Feedback room question',
    });
    // Once per time the wall appears, not on every balance tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex items-center gap-3 rounded-card bg-warm px-4 py-3">
      <div className="min-w-0 flex-1">
        <p className="text-ui-strong font-semibold text-text-primary">
          Out of credits for another question
        </p>
        <p className="mt-0.5 text-caption leading-relaxed text-text-secondary">
          A question here is about {credits(estimate)}; you have {available}.
          Everything so far is saved, and you can still end the session for the
          report.
        </p>
      </div>
      <Link
        className="flex h-8 shrink-0 items-center rounded-button bg-brand px-3 text-ui font-semibold text-white transition hover:bg-brand-alt"
        onClick={() => {
          trackEvent('top_up_clicked', {
            balance: available,
            source: 'room_wall',
          });
        }}
        state={{ back: backToRoom, from: 'room_wall' }}
        to="/billing"
      >
        Top up
      </Link>
    </div>
  );
}
